import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import API from '../utils/api';
import JobCard from '../components/JobCard';
import JobFilters from '../components/JobFilters';
import {
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaBriefcase,
  FaMapMarkerAlt,
  FaTimes,
} from 'react-icons/fa';
import FAQSection from '../components/FAQSection';
import './Jobs.css';

const Jobs = () => {
  // ── STATE (UNCHANGED) ──────────────────────────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    location: searchParams.get('location') || '',
    workMode: searchParams.get('workMode') || '',
    employmentType: searchParams.get('employmentType') || '',
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalJobs: 0,
    limit: 10,
  });

  // ── DATA ARRAYS (UNCHANGED) ────────────────────────────────────────
  const categories = [
    'Software Development',
    'Data & Analytics',
    'Design',
    'Customer Support',
    'Banking & Finance',
    'Marketing',
    'Operations',
    'HR & Recruitment',
  ];

  const workModes = ['On-site', 'Remote', 'Hybrid'];
  const employmentTypes = ['Full-time', 'Part-time', 'Contract', 'Internship', 'Freelance'];

  // ── EFFECTS (UNCHANGED) ───────────────────────────────────────────
  // Update filters when URL parameters change (e.g., from Navbar search)
  useEffect(() => {
    const newFilters = {
      search: searchParams.get('search') || '',
      category: searchParams.get('category') || '',
      location: searchParams.get('location') || '',
      workMode: searchParams.get('workMode') || '',
      employmentType: searchParams.get('employmentType') || '',
    };
    setFilters(newFilters);
    setPagination({ currentPage: 1, totalPages: 1, totalJobs: 0, limit: 10 });
  }, [searchParams]);

  useEffect(() => {
    fetchJobs();
  }, [filters, pagination.currentPage, pagination.limit]);

  // ── HANDLERS (UNCHANGED) ──────────────────────────────────────────
  const fetchJobs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      params.append('page', pagination.currentPage);
      params.append('limit', pagination.limit);

      const { data } = await API.get(`/jobs?${params.toString()}`);
      setJobs(data.jobs);
      setPagination(prev => ({
        ...prev,
        currentPage: data.currentPage,
        totalPages: data.totalPages,
        totalJobs: data.totalJobs,
      }));
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, currentPage: 1 });
  };

  const handleLimitChange = (newLimit) => {
    setPagination({ ...pagination, limit: newLimit, currentPage: 1 });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchJobs();
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      location: '',
      workMode: '',
      employmentType: '',
    });
    setPagination({ ...pagination, currentPage: 1 });
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination({ ...pagination, currentPage: page });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const pages = [];
    const currentPage = pagination.currentPage;
    const totalPages = pagination.totalPages;

    // Always show first page
    pages.push(1);

    // Show pages around current page
    if (currentPage > 3) {
      pages.push('...');
    }

    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      pages.push(i);
    }

    // Show last page
    if (currentPage < totalPages - 2) {
      pages.push('...');
    }
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return (
      <div className="jobs-pagination-row">
        {/* Previous Button */}
        <button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          className="pag-btn"
        >
          <FaChevronLeft size={11} /> Previous
        </button>

        {/* Page Numbers */}
        {pages.map((page, index) =>
          page === '...' ? (
            <span key={`ellipsis-${index}`} className="pag-ellipsis">
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => goToPage(page)}
              className={`pag-number${page === currentPage ? ' active' : ''}`}
            >
              {page}
            </button>
          )
        )}

        {/* Next Button */}
        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === pagination.totalPages}
          className="pag-btn"
        >
          Next <FaChevronRight size={11} />
        </button>
      </div>
    );
  };

  // ── UI HELPERS (new — read-only, no side effects) ─────────────────
  // Build active filter chips for display
  const getActiveFilters = () => {
    const active = [];
    if (filters.search)         active.push({ key: 'search',         label: `"${filters.search}"` });
    if (filters.category)       active.push({ key: 'category',       label: filters.category });
    if (filters.location)       active.push({ key: 'location',       label: filters.location });
    if (filters.workMode)       active.push({ key: 'workMode',       label: filters.workMode });
    if (filters.employmentType) active.push({ key: 'employmentType', label: filters.employmentType });
    return active;
  };
  const activeFilters = getActiveFilters();

  // Quick category pills for the hero strip
  const quickCategories = [
    { icon: '💻', label: 'IT & Software',     value: 'IT & Software Development' },
    { icon: '📱', label: 'Digital Marketing', value: 'Digital Marketing & Branding' },
    { icon: '🎨', label: 'Creative & Design', value: 'Creative & Design' },
    { icon: '📊', label: 'Data & Analytics',  value: 'Data & Analytics' },
    { icon: '💼', label: 'Sales & BizDev',    value: 'Sales & Business Development' },
    { icon: '👥', label: 'Human Resources',   value: 'Human Resources' },
    { icon: '🛒', label: 'E-Commerce',        value: 'E-Commerce' },
    { icon: '🎓', label: 'Education',         value: 'Education & Training' },
    { icon: '⚙️', label: 'Operations',        value: 'Administration & Operations' },
    { icon: '☁️', label: 'Cloud & DevOps',    value: 'Cloud & DevOps' },
  ];

  // ── RENDER ────────────────────────────────────────────────────────
  return (
    <div className="jobs-page-wrapper">

      {/* ====================== HERO SECTION ====================== */}
      <section className="jobs-hero-section">
        {/* Decorative background circles */}
        <div className="hero-bg-circle hero-bg-circle-1" />
        <div className="hero-bg-circle hero-bg-circle-2" />
        <div className="hero-bg-circle hero-bg-circle-3" />

        <div className="hero-inner">
          {/* ── Left: Text + Search ── */}
          <div className="hero-text-col">
            <div className="hero-badge-pill">
              <FaBriefcase size={12} />
              <span>
                {pagination.totalJobs > 0
                  ? `${pagination.totalJobs}+ Active Job Openings`
                  : 'Explore Opportunities'}
              </span>
            </div>

            <h1 className="hero-title">
              Find Your <span className="hero-title-highlight">Dream Job</span><br />Today
            </h1>

            <p className="hero-subtitle">
              Search from thousands of verified job listings across top
              companies in India. Your next opportunity is here.
            </p>

            {/* Search Form — same inputs, same handlers, same onSubmit */}
            <form onSubmit={handleSearch} className="hero-search-form">
              <div className="hero-search-field">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Job title, skills, or company"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
              <div className="hero-location-field">
                <FaMapMarkerAlt className="search-icon" />
                <input
                  type="text"
                  placeholder="Location"
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                />
              </div>
              <button type="submit" className="hero-search-btn">
                Search Jobs
              </button>
            </form>

            {/* Stats pills */}
            <div className="hero-stats-row">
              <div className="hero-stat-pill">
                <span className="stat-dot" /> 10K+ Jobs Listed
              </div>
              <div className="hero-stat-pill">
                <span className="stat-dot" /> 500+ Companies
              </div>
              <div className="hero-stat-pill">
                <span className="stat-dot" /> Fresh Daily Updates
              </div>
            </div>
          </div>

          {/* ── Right: Inline SVG Illustration ── */}
          <div className="hero-illustration-col">
            <svg
              viewBox="0 0 500 400"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="hero-svg-illustration"
            >
              {/* Minimalist illustration matching the SaaS screenshot */}
              {/* Large backdrop blob */}
              <circle cx="250" cy="200" r="160" fill="rgba(255,255,255,0.06)" />
              <circle cx="250" cy="200" r="120" fill="rgba(255,255,255,0.08)" />
              
              {/* Laptop Base */}
              <rect x="130" y="270" width="240" height="12" rx="6" fill="rgba(255,255,255,0.6)" />
              <rect x="150" y="160" width="200" height="110" rx="10" fill="rgba(255,255,255,0.95)" />
              <rect x="160" y="170" width="180" height="85" rx="6" fill="#f1f5f9" />
              
              {/* Person Head */}
              <circle cx="250" cy="110" r="35" fill="rgba(255,255,255,0.9)" />
              {/* Eyes */}
              <circle cx="235" cy="105" r="4" fill="#00c2ab" />
              <circle cx="265" cy="105" r="4" fill="#00c2ab" />
              {/* Smile */}
              <path d="M238 118 Q250 128 262 118" stroke="#00c2ab" strokeWidth="3" strokeLinecap="round" fill="none" />
              
              {/* Torso */}
              <path d="M190 270 Q190 200 250 200 Q310 200 310 270" fill="rgba(255,255,255,0.3)" />

              {/* Floating ID Card */}
              <g transform="translate(70, 140)" className="float-anim-1">
                <rect width="70" height="50" rx="8" fill="rgba(255,255,255,0.95)" />
                <rect x="12" y="15" width="24" height="20" rx="4" fill="#fde68a" />
                <rect x="42" y="18" width="20" height="4" rx="2" fill="#cbd5e1" />
                <rect x="42" y="28" width="14" height="4" rx="2" fill="#cbd5e1" />
              </g>

              {/* Floating Briefcase */}
              <g transform="translate(360, 110)" className="float-anim-2">
                <rect width="60" height="46" rx="8" fill="rgba(255,255,255,0.95)" />
                <rect x="20" y="-8" width="20" height="12" rx="4" fill="none" stroke="rgba(255,255,255,0.95)" strokeWidth="4" />
                <rect x="10" y="10" width="40" height="26" rx="4" fill="#fca5a5" />
                <rect x="22" y="10" width="16" height="26" fill="#ef4444" />
              </g>
              
              {/* Floating Browser Tab */}
              <g transform="translate(350, 220)" className="float-anim-1" style={{ animationDelay: '1s' }}>
                <rect width="70" height="40" rx="6" fill="rgba(255,255,255,0.95)" />
                <circle cx="15" cy="12" r="3" fill="#cbd5e1" />
                <circle cx="25" cy="12" r="3" fill="#cbd5e1" />
                <circle cx="35" cy="12" r="3" fill="#cbd5e1" />
                <rect x="10" y="22" width="40" height="6" rx="3" fill="#00c2ab" />
              </g>

              {/* Little stars/sparkles */}
              <path d="M90 80 l3 8 l8 3 l-8 3 l-3 8 l-3 -8 l-8 -3 l8 -3 z" fill="rgba(255,255,255,0.6)" />
              <path d="M380 60 l2 5 l5 2 l-5 2 l-2 5 l-2 -5 l-5 -2 l5 -2 z" fill="rgba(255,255,255,0.4)" />
              <path d="M120 320 l2 6 l6 2 l-6 2 l-2 6 l-2 -6 l-6 -2 l6 -2 z" fill="rgba(255,255,255,0.3)" />
            </svg>
          </div>
        </div>

        {/* Wave transition into page body */}
        <div className="hero-wave">
          <svg
            viewBox="0 0 1440 56"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M0,28 C320,56 1120,0 1440,28 L1440,56 L0,56 Z" fill="#f0f9f8" />
          </svg>
        </div>
      </section>

      {/* =================== QUICK CATEGORY STRIP ================= */}
      <div className="category-quick-strip">
        <div className="category-strip-inner">
          <span className="strip-label">Browse by:</span>
          {quickCategories.map((cat) => (
            <button
              key={cat.value}
              onClick={() => handleFilterChange('category', cat.value)}
              className={`cat-quick-pill${filters.category === cat.value ? ' active' : ''}`}
            >
              <span>{cat.icon}</span>
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* =================== MAIN CONTENT AREA =================== */}
      <div className="jobs-body-bg">
        <div className="jobs-main-layout">

          {/* ── FILTER SIDEBAR (Desktop) ── */}
          <aside className="jobs-filter-panel">
            <JobFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={clearFilters}
            />
          </aside>

          {/* ── RESULTS PANEL ── */}
          <main className="jobs-results-panel">

            {/* Category filter banner */}
            {filters.category && (
              <div className="category-banner">
                <div>
                  <h1 className="category-banner-title">{filters.category}</h1>
                  <p className="category-banner-sub">
                    {pagination.totalJobs} job{pagination.totalJobs !== 1 ? 's' : ''} available
                  </p>
                </div>
                <button
                  onClick={() => handleFilterChange('category', '')}
                  className="cat-banner-clear"
                >
                  <FaTimes size={13} /> Clear
                </button>
              </div>
            )}

            {/* Results header */}
            <div className="results-header-card">
              <div className="results-left">
                <div className="live-dot" />
                <span className="results-count-num">{pagination.totalJobs}</span>
                <span className="results-count-text">Jobs Found</span>
              </div>
              <div className="results-right">
                {/* Jobs per page selector (UNCHANGED) */}
                <span className="show-label">Show:</span>
                <select
                  value={pagination.limit}
                  onChange={(e) => handleLimitChange(Number(e.target.value))}
                  className="limit-select"
                >
                  <option value={5}>5 jobs</option>
                  <option value={10}>10 jobs</option>
                  <option value={20}>20 jobs</option>
                  <option value={50}>50 jobs</option>
                </select>
              </div>
            </div>

            {/* Active filter chips */}
            {activeFilters.length > 0 && (
              <div className="active-filters-row">
                <span className="active-filters-label">Active:</span>
                {activeFilters.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => handleFilterChange(f.key, '')}
                    className="active-filter-chip"
                  >
                    {f.label}
                    <FaTimes size={9} className="chip-x" />
                  </button>
                ))}
                <button onClick={clearFilters} className="clear-all-link">
                  Clear all
                </button>
              </div>
            )}

            {/* ── Job Cards / Loading / Empty ── */}
            {loading ? (
              <div className="jobs-loading-box">
                <div className="teal-spinner" />
                <p>Finding the best jobs for you...</p>
              </div>
            ) : jobs.length > 0 ? (
              <>
                <div className="job-cards-list">
                  {jobs.map((job) => (
                    <JobCard key={job._id} job={job} />
                  ))}
                </div>

                {/* Pagination (renderPagination — UNCHANGED logic) */}
                {renderPagination()}

                {/* Pagination info text */}
                {pagination.totalPages > 1 && (
                  <p className="pagination-info-text">
                    Showing page {pagination.currentPage} of {pagination.totalPages}
                    {' · '}
                    {pagination.totalJobs} total jobs
                  </p>
                )}
              </>
            ) : (
              <div className="no-jobs-card">
                <div className="no-jobs-icon">
                  <FaBriefcase size={34} />
                </div>
                <h3>No Jobs Found</h3>
                <p>
                  We couldn't find any jobs matching your current filters.
                  <br />
                  Try adjusting your search or clearing the filters.
                </p>
                <button onClick={clearFilters} className="clear-filters-btn">
                  Clear Filters &amp; Try Again
                </button>
              </div>
            )}
          </main>
        </div>
      </div>
      
      {/* ── FAQ SECTION ── */}
      <FAQSection 
        title="Find Jobs FAQ"
        subtitle="Common questions about searching and applying for jobs."
        faqs={[
          { q: 'How do I search for a job?', a: 'You can use the search bar at the top to enter keywords, and use the filters on the left to narrow down by category, location, and salary.' },
          { q: 'How do I apply for a job?', a: 'Click on a job card to view the details, and then click the Apply button. Ensure your profile is fully filled out before applying.' },
          { q: 'Can I save jobs for later?', a: 'Yes! Simply click the bookmark icon on any job card, and you can view it later in your Saved Jobs page.' },
          { q: 'How do I track my applications?', a: 'You can track all your submitted applications through your dashboard under the My Applications section.' },
          { q: 'Are there remote job options?', a: 'Yes, select Remote under the Job Type filter to see all remote positions available.' }
        ]}
      />
    </div>
  );
};

export default Jobs;
